import React, { useState, useEffect, useRef } from "react";
import './i18n/config';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import EmergencyView from "./pages/EmergencyView";
import { protectedRoutes, adminRoutes, LandingPage } from "./config/routes";
import FoodPriceCompare from "./pages/FoodPriceCompare";
import RidePriceCompare from "./pages/RidePriceCompare";
import GroceryPriceCompare from "./pages/GroceryPriceCompare";
import WellnessLanding from "./pages/wellness/WellnessLanding";
import WellnessBooking from "./pages/wellness/WellnessBooking";
import WellnessConfirmation from "./pages/wellness/WellnessConfirmation";
import WellnessLookup from "./pages/wellness/WellnessLookup";
import WellnessAdmin from "./pages/wellness/WellnessAdmin";
import axios from "axios";
import secureStorage from "./utils/secureStorage";

// Determine backend URL: prefer same-origin in production (e.g., medhasvi.ai),
// fall back to env-baked URL for preview/dev.
const getBackendUrl = () => {
  if (typeof window !== "undefined") {
    const host = window.location.hostname;
    // For any non-preview, non-localhost host, call APIs on the same origin.
    // This handles custom domains (medhasvi.ai) where the proxy serves /api too.
    const isPreview = host.includes("preview.emergentagent.com");
    const isLocal = host === "localhost" || host === "127.0.0.1";
    if (!isPreview && !isLocal) {
      return window.location.origin;
    }
  }
  return process.env.REACT_APP_BACKEND_URL;
};

const BACKEND_URL = getBackendUrl();
export const API = `${BACKEND_URL}/api`;

axios.interceptors.request.use(
  (config) => {
    const token = secureStorage.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      secureStorage.clearToken();
      delete axios.defaults.headers.common['Authorization'];
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

// Analytics Tracker Component
function AnalyticsTracker() {
  const location = useLocation();
  const lastTrackedPath = useRef('');

  useEffect(() => {
    if (lastTrackedPath.current === location.pathname) return;
    lastTrackedPath.current = location.pathname;

    axios.post(`${API}/analytics/track-visit`, {
      page: location.pathname,
      referrer: document.referrer || 'direct',
      user_agent: navigator.userAgent
    }).catch(() => {});
  }, [location.pathname]);

  return null;
}

// Protected Route wrapper component
const ProtectedRoute = ({ user, setUser, component: Component }) => {
  if (!user) return <Navigate to="/" />;
  return <Component user={user} setUser={setUser} />;
};

// Admin Route wrapper component  
const AdminRoute = ({ user, setUser, component: Component }) => {
  if (!user || user.role !== 'super_admin') return <Navigate to="/" />;
  return <Component user={user} setUser={setUser} />;
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(() => secureStorage.hasToken());

  useEffect(() => {
    const token = secureStorage.getToken();
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      axios.get(`${API}/auth/me`)
        .then(response => setUser(response.data))
        .catch(() => {
          secureStorage.clearToken();
          delete axios.defaults.headers.common['Authorization'];
        })
        .finally(() => setLoading(false));
    }
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-cyan-50 via-white to-teal-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-teal-600 mx-auto"></div>
          <p className="mt-4 text-teal-700 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <BrowserRouter>
        <AnalyticsTracker />
        <Routes>
          {/* Landing page */}
          <Route 
            path="/" 
            element={!user ? <LandingPage setUser={setUser} /> : <Navigate to="/dashboard" />} 
          />
          
          {/* Protected routes */}
          {protectedRoutes.map(({ path, component }) => (
            <Route
              key={path}
              path={path}
              element={<ProtectedRoute user={user} setUser={setUser} component={component} />}
            />
          ))}
          
          {/* Admin routes */}
          {adminRoutes.map(({ path, component }) => (
            <Route
              key={path}
              path={path}
              element={<AdminRoute user={user} setUser={setUser} component={component} />}
            />
          ))}
          
          {/* Public emergency view */}
          <Route path="/emergency-view/:profileId" element={<EmergencyView />} />
          
          {/* Public standalone mini-apps */}
          <Route path="/food-compare" element={<FoodPriceCompare />} />
          <Route path="/ride-compare" element={<RidePriceCompare />} />
          <Route path="/grocery-compare" element={<GroceryPriceCompare />} />
          <Route path="/wellness" element={<WellnessLanding />} />
          <Route path="/wellness/rooms" element={<WellnessLanding />} />
          <Route path="/wellness/treatments" element={<WellnessLanding />} />
          <Route path="/wellness/book" element={<WellnessBooking />} />
          <Route path="/wellness/confirmation/:code" element={<WellnessConfirmation />} />
          <Route path="/wellness/lookup" element={<WellnessLookup />} />
          <Route path="/wellness/admin" element={<WellnessAdmin />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
